import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, TrendingUp, Users, Star, Check, ArrowRight,
  Phone, Mail, Globe, Sparkles, Award, Target, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Images
const TOBY_IMAGE = "https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/92iyvmuc_Tobi%20CBO%20Puppoto.jpg";

const PuppotoPartnersInvite = () => {
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/puppoto/sponsor-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (err) {
      toast.error('Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { icon: Users, title: "50,000+ Dog Lovers", desc: "Targeted audience of passionate pet owners" },
    { icon: Target, title: "Local Targeting", desc: "Reach customers in your specific area" },
    { icon: BarChart3, title: "Monthly Reports", desc: "Track your visibility and engagement" },
    { icon: Award, title: "Verified Badge", desc: "Stand out as a trusted partner" }
  ];

  const tiers = [
    { name: "Bronze", price: "$500", monthly: "$500/mo", color: "from-amber-600 to-amber-700" },
    { name: "Silver", price: "$1,000", monthly: "$1,000/mo", color: "from-gray-400 to-gray-500" },
    { name: "Gold", price: "$2,500", monthly: "$2,500/mo", color: "from-yellow-400 to-yellow-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-amber-500/20" />
        <div className="container mx-auto px-4 py-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <img src={TOBY_IMAGE} alt="Toby" className="w-16 h-16 rounded-full border-4 border-orange-400" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Partner with <span className="text-orange-400">Puppoto</span>
              </h1>
            </div>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
              Australia's fastest-growing platform for dog lovers. 
              Connect your business with 50,000+ passionate pet owners.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Launching May 2026</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> 16 Languages</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Australia-Wide</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Benefits */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-12">Why Partner With Us?</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <item.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-gray-400 text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Sponsorship Tiers */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-4">Sponsorship Tiers</h2>
        <p className="text-gray-400 text-center mb-12 max-w-xl mx-auto">
          Choose the level that's right for your business. All tiers include listing in our directory.
        </p>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10"
            >
              <div className={`bg-gradient-to-r ${tier.color} py-4 text-center`}>
                <h3 className="text-xl font-bold text-white">{tier.name}</h3>
              </div>
              <div className="p-6 text-center">
                <div className="text-3xl font-bold text-white mb-1">{tier.price}</div>
                <div className="text-gray-400 text-sm mb-4">setup + {tier.monthly}</div>
                <ul className="text-left space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Directory Listing</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Verified Badge</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Social Mentions</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Monthly Reports</li>
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Expression of Interest Form */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 text-center border border-white/10"
            >
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
              <p className="text-gray-300 mb-6">
                We've received your expression of interest. Our partnerships team will be in touch within 24-48 hours.
              </p>
              <p className="text-gray-400 text-sm">
                In the meantime, follow us for updates!
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10"
            >
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                <Sparkles className="w-6 h-6 inline mr-2 text-orange-400" />
                Express Your Interest
              </h2>
              <p className="text-gray-400 text-center mb-8">
                Be among our founding partners and lock in early-bird rates
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">Business Name *</label>
                    <input
                      type="text"
                      value={form.business_name}
                      onChange={(e) => setForm({...form, business_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="Your Business"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">Your Name *</label>
                    <input
                      type="text"
                      value={form.contact_name}
                      onChange={(e) => setForm({...form, contact_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="Your Name"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="email@business.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="04XX XXX XXX"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">What type of business?</label>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({...form, interest: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="" className="text-gray-800">Select...</option>
                    <option value="vet" className="text-gray-800">Veterinary Clinic</option>
                    <option value="groomer" className="text-gray-800">Groomer / Dog Wash</option>
                    <option value="trainer" className="text-gray-800">Training / Obedience</option>
                    <option value="boarding" className="text-gray-800">Boarding / Day Care</option>
                    <option value="pet_shop" className="text-gray-800">Pet Shop / Supplies</option>
                    <option value="pet_food" className="text-gray-800">Pet Food Brand</option>
                    <option value="breeder" className="text-gray-800">Breeder</option>
                    <option value="other" className="text-gray-800">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Anything else?</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="Tell us about your business..."
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 text-lg bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold rounded-xl"
                >
                  {submitting ? 'Submitting...' : 'Submit Expression of Interest'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="container mx-auto px-4 py-8 text-center text-gray-500 text-sm">
        <p>Puppoto by Lionheart Digital | 🇦🇺 Made in Australia</p>
        <p className="mt-2">Launching May 2026</p>
      </div>
    </div>
  );
};

export default PuppotoPartnersInvite;
